/**
 * Coordinates deep-linking the Medical Necessity PDF to a specific page.
 *
 * Evidence-fact `DocChunk#N` references (right panel) navigate the left-hand
 * clinical PDF, which lives in a sibling subtree. This context is the bridge:
 * the PDF panel registers its page-navigation callback once its document has
 * loaded, and anything can call {@link usePdfNavigation}.`goToPage` from there.
 * A page requested before the document is ready is remembered and flushed on
 * registration, so an eager click never silently drops.
 * @module medicalNecessityAnalysis/context/PdfNavigationContext
 */
import React, {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useRef,
} from "react";

/** A function that scrolls the clinical PDF to a 1-based page. */
type PdfNavigate = (page: number) => void;

/** Values exposed to the PDF panel and evidence references. */
interface PdfNavigationContextValue {
  /** Navigates the clinical PDF to a 1-based page (queued if not yet loaded). */
  goToPage: (page: number) => void;
  /** Registers the PDF's navigate callback; flushes any queued page. */
  registerNavigate: (navigate: PdfNavigate) => void;
}

const PdfNavigationContext = createContext<PdfNavigationContextValue | null>(
  null,
);

/** Props for {@link PdfNavigationProvider}. */
interface PdfNavigationProviderProps {
  /** Screen content that contains the PDF panel and the evidence panel. */
  children: React.ReactNode;
}

/**
 * Provides PDF page navigation to the Medical Necessity screen.
 *
 * @param props - Screen children (the PDF panel and the NCCN review panel)
 * @returns A provider wrapping the screen
 */
export const PdfNavigationProvider: React.FC<PdfNavigationProviderProps> = ({
  children,
}) => {
  const navigateRef = useRef<PdfNavigate | null>(null);
  const pendingPageRef = useRef<number | null>(null);

  /** Flushes a previously queued page once the PDF reports it is ready. */
  const registerNavigate = useCallback((navigate: PdfNavigate) => {
    navigateRef.current = navigate;
    if (pendingPageRef.current !== null) {
      const page = pendingPageRef.current;
      pendingPageRef.current = null;
      navigate(page);
    }
  }, []);

  /** Navigates now, or remembers the page if the PDF is not ready yet. */
  const goToPage = useCallback((page: number) => {
    if (navigateRef.current) {
      navigateRef.current(page);
    } else {
      pendingPageRef.current = page;
    }
  }, []);

  const value = useMemo<PdfNavigationContextValue>(
    () => ({ goToPage, registerNavigate }),
    [goToPage, registerNavigate],
  );

  return (
    <PdfNavigationContext.Provider value={value}>
      {children}
    </PdfNavigationContext.Provider>
  );
};

/**
 * Returns the shared PDF navigation actions.
 *
 * @returns The `goToPage` / `registerNavigate` pair
 * @throws Error when used outside {@link PdfNavigationProvider}
 */
export const usePdfNavigation = (): PdfNavigationContextValue => {
  const context = useContext(PdfNavigationContext);
  if (!context) {
    throw new Error(
      "usePdfNavigation must be used within PdfNavigationProvider",
    );
  }
  return context;
};
